/**
 * PolicyIntel UI v2 — personal change inbox.
 * An in-app view of observed changes for server-registered watch stocks.
 * This is NOT a push delivery log and has no read/unread authority.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import {
  getLiveSummaryChanges, getLiveSummaryChangeEvents, getLiveSummaryHoldings,
  type LiveSummaryChangeItem, type LiveSummaryChangeEvent,
} from '../api/liveSummaryApi';
import { trackUxEvent } from '../analytics/analytics';
import { getWebPushBrowserStatus, getWebPushServerStatus, enableWebPush, sendWebPushTest, webPushSupported } from '../components/alerts/webPushClient';

type Rec = Record<string, unknown>;
type Tab = 'CHECK' | 'TODAY' | 'HISTORY';
type Grade = 'DANGER' | 'WARNING' | 'INFO';
type InboxItem = {
  id: string;
  key: string;
  ticker: string;
  name: string;
  title: string;
  detail: string;
  before: string;
  after: string;
  at: string;
  grade: Grade;
  source: 'change' | 'event';
};
const C = {
  bg: '#F6F8FB', white: '#FFFFFF', ink: '#122033', muted: '#607086', line: '#E2E8F0',
  navy: '#10263F', blue: '#2F6BFF', risk: '#D84C57', watch: '#D58A16', unknown: '#77869A',
};
const toText = (v: unknown): string => v === null || v === undefined ? '' : String(v).trim();
const pick = (...values: unknown[]): string => values.map(toText).find(Boolean) ?? '';
const codeOf = (v: unknown): string => toText(v).match(/^A?(\d{6})$/)?.[1] ?? '';
function stockCode(v: Rec): string {
  const directly = codeOf(v.ticker ?? v.stockCode ?? v.stock_code);
  if (directly) return directly;
  const scope = pick(v.entityType, v.entity_type).toUpperCase();
  if (scope !== 'STOCK' && scope !== 'HOLDING') return '';
  return pick(v.entityKey, v.entity_key).match(/(?:^|\D)(\d{6})(?:\D|$)/)?.[1] ?? '';
}
function gradeOf(v: unknown): Grade {
  const s = toText(v).toUpperCase();
  if (s === 'DANGER' || s === 'CRITICAL') return 'DANGER';
  if (s === 'WARNING' || s === 'WARN') return 'WARNING';
  return 'INFO';
}
function asDate(v: unknown): number {
  const n = Date.parse(toText(v));
  return Number.isFinite(n) ? n : 0;
}
function seoulDay(v: unknown): string {
  const n = asDate(v);
  if (!n) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(n));
  const part = (type: string): string => parts.find(x => x.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}
function timeText(v: string): string {
  const n = asDate(v);
  if (!n) return '발생 시각 확인 필요';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(n));
}
function toInbox(v: LiveSummaryChangeItem | LiveSummaryChangeEvent, source: 'change' | 'event', names: Map<string, string>): InboxItem | null {
  const raw = v as Rec;
  const ticker = stockCode(raw);
  // Strict membership is checked before this function is called.
  if (!ticker || !names.has(ticker)) return null;
  const id = pick(raw.eventId, raw.id);
  const title = pick(raw.title, raw.changeLayerLabel, raw.eventType, raw.changeType, '관측된 변화');
  const detail = pick(raw.reason, raw.message, raw.summary, '상세 근거가 아직 제공되지 않았습니다.');
  const at = pick(raw.occurredAt, raw.detectedAt, raw.sourceEventAt);
  return {
    id, ticker, name: names.get(ticker) ?? ticker, title, detail,
    before: pick(raw.before, raw.previousValue),
    after: pick(raw.after, raw.currentValue), at,
    grade: gradeOf(raw.severity), source,
    key: id ? `${ticker}:${id}` : `${ticker}:${pick(raw.changeType,raw.eventType)}:${title}:${at}`,
  };
}
function ordered(items: InboxItem[]): InboxItem[] {
  const ranked: Record<Grade, number> = { DANGER: 0, WARNING: 1, INFO: 2 };
  return [...items].sort((a, b) => ranked[a.grade] - ranked[b.grade] || asDate(b.at) - asDate(a.at));
}
function buildItems(
  changes: LiveSummaryChangeItem[],
  events: LiveSummaryChangeEvent[],
  names: Map<string, string>,
): InboxItem[] {
  // The same event can appear in both endpoints. Prefer the enriched 'changes' record.
  const unique = new Map<string, InboxItem>();
  for (const event of events) {
    const item = toInbox(event, 'event', names);
    if (item) unique.set(item.key, item);
  }
  for (const change of changes) {
    const item = toInbox(change, 'change', names);
    if (item) unique.set(item.key, item);
  }
  return ordered(Array.from(unique.values()));
}

export default function AlertInboxPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('CHECK');
  const [items, setItems] = useState<InboxItem[]>([]);
  const [watchCount, setWatchCount] = useState(0);
  const [busy, setBusy] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [partialError, setPartialError] = useState('');
  const [windowLabel, setWindowLabel] = useState('최근 관측 기준');
  const requestRef = useRef(0);
  // Status is verified independently of the observed-change inbox. Neither a
  // browser subscription nor a server send count proves notification delivery.
  type PushState = 'checking' | 'unsupported' | 'blocked' | 'unconfigured' | 'ready' | 'active' | 'error';
  const [pushState, setPushState] = useState<PushState>('checking');
  const [pushKey, setPushKey] = useState('');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNotice, setPushNotice] = useState('');
  const pushRequestRef = useRef(0);
  const pushActionLock = useRef(false);

  const refreshPush = useCallback(async () => {
    const request = ++pushRequestRef.current;
    if (!webPushSupported()) {
      setPushState('unsupported');
      return;
    }
    setPushState('checking');
    try {
      const [server, browser] = await Promise.all([
        getWebPushServerStatus(), getWebPushBrowserStatus(),
      ]);
      if (request !== pushRequestRef.current) return;
      setPushKey(server.vapidPublicKey);
      if (browser.permission === 'denied') setPushState('blocked');
      else if (!server.serverConfigured || !server.vapidPublicKey) setPushState('unconfigured');
      else if (server.subscribed && browser.subscribed && browser.permission === 'granted') setPushState('active');
      else setPushState('ready');
    } catch {
      if (request === pushRequestRef.current) setPushState('error');
    }
  }, []);

  // This handler runs ONLY after an explicit user click, keeping browser
  // Notification.requestPermission() within the user activation gesture.
  const subscribePush = async () => {
    if (pushActionLock.current || pushState !== 'ready' || !pushKey) return;
    pushActionLock.current = true;
    setPushBusy(true); setPushNotice('');
    try {
      await enableWebPush(pushKey);
      const [server, browser] = await Promise.all([
        getWebPushServerStatus(), getWebPushBrowserStatus(),
      ]);
      if (!(server.subscribed && browser.subscribed && browser.permission === 'granted')) {
        throw new Error('구독 요청 후 서버와 브라우저의 등록 상태가 일치하지 않습니다.');
      }
      setPushState('active');
      setPushNotice('이 브라우저의 웹푸시 구독이 확인됐습니다. 변화 알림 발송·수신은 별도 확인이 필요합니다.');
      trackUxEvent('push_enabled', { source: 'alert-inbox', confirmed_server_subscription: true });
    } catch (error: unknown) {
      setPushNotice(error instanceof Error ? error.message : '웹푸시 구독에 실패했습니다.');
      await refreshPush();
    } finally {
      pushActionLock.current = false;
      setPushBusy(false);
    }
  };

  const requestTestPush = async () => {
    if (pushActionLock.current || pushState !== 'active') return;
    pushActionLock.current = true;
    setPushBusy(true); setPushNotice('');
    try {
      const sent = await sendWebPushTest();
      setPushNotice(sent > 0
        ? `서버가 테스트 푸시 ${sent}건을 발송 처리했습니다. 브라우저 수신은 별도로 확인해야 합니다.`
        : '서버에서 테스트 푸시 발송 대상이 확인되지 않았습니다.');
      trackUxEvent('web_push_test_requested', {source:'alert-inbox',server_sent_count:sent});
    } catch {
      setPushNotice('테스트 푸시 요청에 실패했습니다. 서버 및 권한 상태를 확인해 주세요.');
    } finally {
      pushActionLock.current = false;
      setPushBusy(false);
    }
  };


  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setBusy(true);
    setError(''); setPartialError('');
    // Membership MUST be confirmed by the server. Without it no candidate is shown.
    const holdings = await getLiveSummaryHoldings({ refresh: true, timeoutMs: 12000 })
      .then(value => ({ ok: true as const, value }))
      .catch(() => ({ ok: false as const, value: [] }));
    if (request !== requestRef.current) return;
    if (!holdings.ok) {
      setItems([]); setLoaded(false);
      setError('내 관심종목 목록을 조회하지 못했습니다. 로그인 상태와 서버 연결을 확인해 주세요.');
      setBusy(false);
      return;
    }
    const names = new Map<string, string>();
    for (const holding of holdings.value) {
      const row = holding as Rec;
      const ticker = codeOf(row.ticker);
      if (ticker) names.set(ticker, pick(row.name, row.corpName, ticker));
    }
    setWatchCount(names.size);
    if (!names.size) {
      setItems([]); setLoaded(true); setBusy(false);
      return;
    }
    const [changeResult, eventResult] = await Promise.allSettled([
      getLiveSummaryChanges({ limit: 100, timeoutMs: 9000 }),
      getLiveSummaryChangeEvents({ limit: 100, timeoutMs: 9000 }),
    ]);
    if (request !== requestRef.current) return;
    if (changeResult.status === 'rejected' && eventResult.status === 'rejected') {
      setItems([]); setLoaded(false);
      setError('변화 데이터 조회에 실패했습니다. 다시 시도해 주세요.');
      setBusy(false);
      return;
    }
    if (changeResult.status === 'rejected' || eventResult.status === 'rejected') {
      setPartialError('일부 변화 원천의 조회가 실패했습니다. 현재 표시된 결과가 전체 기록을 뜻하지 않습니다.');
    }
    const response = changeResult.status === 'fulfilled' ? changeResult.value : null;
    const recent = response && Array.isArray(response.changes) ? response.changes : [];
    const history = eventResult.status === 'fulfilled' && Array.isArray(eventResult.value) ? eventResult.value : [];
    const apiWindow = pick(response?.window);
    setWindowLabel(apiWindow === 'SINCE_YESTERDAY_KST' ? '어제 이후 관측' : '서버 조회 범위 기준');
    const final = buildItems(recent, history, names);
    setItems(final); setLoaded(true); setBusy(false);
    trackUxEvent('alert_inbox_loaded', {
      screen_name: 'alerts', monitored_stock_count: names.size,
      matched_event_count: final.length, partial_data: changeResult.status === 'rejected' || eventResult.status === 'rejected',
    });
  }, []);

  useEffect(() => {
    trackUxEvent('alert_inbox_view', { screen_name: 'alerts', source: 'ui-v2' });
    void load();
    void refreshPush();
    const onVisible = () => { if (document.visibilityState === 'visible') { void load(); void refreshPush(); } };
    const onWatchChange = () => { void load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('policy-intel:watchlist-updated', onWatchChange);
    const changeRequests=requestRef;
    const pushRequests=pushRequestRef;
    return () => {
      changeRequests.current++;
      pushRequests.current++;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('policy-intel:watchlist-updated', onWatchChange);
    };
  }, [load, refreshPush]);

  const today = seoulDay(new Date().toISOString());
  const grouped = useMemo(() => ({
    // Priority means recently reported severe events, not all historical warnings.
    CHECK: items.filter(x => x.grade !== 'INFO' && (x.source === 'change' || seoulDay(x.at) === today)),
    TODAY: items.filter(x => seoulDay(x.at) === today && x.grade === 'INFO'),
    HISTORY: items.filter(x => seoulDay(x.at) !== today || !x.at),
  }), [items, today]);
  const current = grouped[tab];
  const view = (item: InboxItem) => {
    trackUxEvent('alert_open', {
      stock_code: item.ticker,
      change_event_id: item.id || undefined,
      source: 'in_app_change_inbox',
      severity: item.grade,
    });
    const params = new URLSearchParams({ tab: 'CHANGE' });
    if (item.id) params.set('changeEventId', item.id);
    navigate(`/stock/${item.ticker}?${params.toString()}`);
  };
  const filters: Array<{ key: Tab; title: string; count: number; helper: string }> = [
    { key: 'CHECK', title: '지금 확인', count: grouped.CHECK.length, helper: '관측된 주의·위험 이벤트' },
    { key: 'TODAY', title: '오늘', count: grouped.TODAY.length, helper: '오늘 발생이 확인된 정보성 변화' },
    { key: 'HISTORY', title: '기록', count: grouped.HISTORY.length, helper: '조회 가능한 이전 기록 및 시각 미확인' },
  ];
  return <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: C.bg, color: C.ink, py: { xs: 2, md: 4 } }}>
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
        <Box>
          <Typography sx={{ color: C.blue, fontWeight: 850, fontSize: 12, letterSpacing: '.08em' }}>ACTION INBOX</Typography>
          <Typography component="h1" sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 850, letterSpacing: '-.045em', mt: .5 }}>내 종목 변화</Typography>
          <Typography sx={{ fontSize: 14, color: C.muted, lineHeight: 1.8, mt: .8 }}>
            등록한 종목에서 확인된 변화 중 먼저 볼 내용을 모았습니다.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Button variant="outlined" startIcon={busy ? <CircularProgress size={16}/> : <RefreshRoundedIcon/>}
            disabled={busy} onClick={() => void load()}
            sx={{ color: C.navy, borderColor: C.line, textTransform: 'none', fontWeight: 800, borderRadius: 2 }}>
            새로 확인
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ mt: 2.5, bgcolor: C.white, border: `1px solid ${C.line}`, p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
          <NotificationsActiveRoundedIcon sx={{ color: C.blue }}/>
          <Typography sx={{ fontSize: 15, fontWeight: 820 }}>감시 종목 {loaded ? watchCount : '확인 중'}</Typography>
          <Chip size="small" label={windowLabel} variant="outlined" sx={{ fontSize: 11.5, maxWidth: '100%' }}/>
        </Stack>
        <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 1, lineHeight: 1.75 }}>
          이 화면은 서버가 관측한 변화 기록입니다. 푸시 발송·수신 또는 읽음 확인 이력을 의미하지 않습니다.
          발생 시각·근거가 없는 정보는 확정하지 않습니다.
        </Typography>
      </Box>
      <Box sx={{ mb: 2, p: { xs: 2, md: 2.5 }, border: `1px solid ${C.line}`,
        borderRadius: 3, bgcolor: C.white }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between" gap={2}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 830 }}>변화 알림 받기</Typography>
            <Typography sx={{ fontSize: 12.5, mt: .6, color: C.muted, lineHeight: 1.75 }}>
              {pushState === 'active' ? '이 브라우저와 서버의 구독이 확인됐습니다.'
                : pushState === 'ready' ? '원할 때만 브라우저 알림을 켤 수 있습니다.'
                : pushState === 'blocked' ? '브라우저에서 알림이 차단됐습니다. 사이트 권한 설정을 확인해 주세요.'
                : pushState === 'unsupported' ? '현재 브라우저에서 웹푸시를 사용할 수 없습니다.'
                : pushState === 'unconfigured' ? '서버 웹푸시 설정을 확인해야 합니다.'
                : pushState === 'error' ? '구독 상태 조회에 실패했습니다. 알림 설정을 확인해 주세요.'
                : '웹푸시 구독 상태를 확인하고 있습니다.'}
            </Typography>
            <Typography sx={{ color: C.unknown, fontSize: 12, mt: .4 }}>
              구독 확인은 푸시 발송·수신·읽음의 증거가 아닙니다.
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {pushState === 'ready' && <Button variant="contained" disabled={pushBusy || !pushKey}
              onClick={() => { void subscribePush(); }}
              sx={{ bgcolor: C.blue, textTransform:'none', borderRadius: 2, fontWeight: 800 }}>
              {pushBusy ? '설정 중…' : '웹푸시 켜기'}
            </Button>}
            {pushState === 'active' && <Button variant="outlined" disabled={pushBusy}
              onClick={() => { void requestTestPush(); }}
              sx={{ borderColor:C.line, color:C.navy, textTransform:'none', borderRadius:2 }}>
              {pushBusy ? '테스트 중…' : '테스트 알림 보내기'}
            </Button>}
            <Button variant="text" onClick={() => navigate('/mypage?tab=alerts')}
              sx={{ color:C.blue, textTransform:'none' }}>알림 상세 설정</Button>
          </Stack>
        </Stack>
        {pushNotice && <Alert severity={pushState === 'active' ? 'info' : 'warning'} sx={{ mt: 1.5 }}>{pushNotice}</Alert>}
      </Box>
      <Box role="tablist" aria-label="변화 분류" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.2, my: 2.4 }}>
        {filters.map(filter => <Button key={filter.key} role="tab" aria-selected={tab === filter.key}
          onClick={() => { setTab(filter.key); trackUxEvent('alert_inbox_tab_view', { tab: filter.key }); }}
          sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 2, borderRadius: 2.5,
            border: `1px solid ${tab === filter.key ? C.blue : C.line}`,
            bgcolor: tab === filter.key ? '#EEF4FF' : C.white, color: C.ink, textTransform: 'none' }}>
          <Box sx={{ textAlign: 'left', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 850, fontSize: 15 }}>{filter.title} <Box component="span" sx={{ color: C.blue }}>{loaded ? filter.count : '—'}</Box></Typography>
            <Typography sx={{ fontSize: 12, mt: .4, color: C.muted }}>{filter.helper}</Typography>
          </Box>
        </Button>)}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={() => void load()}>다시 조회</Button>}>{error}</Alert>}
      {partialError && <Alert severity="warning" sx={{ mb: 2 }}>{partialError}</Alert>}
      <Box role="tabpanel" sx={{ border: `1px solid ${C.line}`, borderRadius: 3, bgcolor: C.white, overflow: 'hidden' }}>
        {busy && !loaded ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress/><Typography sx={{ mt: 2, color: C.muted }}>실제 감시종목의 변화를 확인하고 있습니다.</Typography></Box>
          : error ? <Box sx={{ p: 3 }}><Typography sx={{ color: C.muted }}>현재 목록을 표시할 수 없습니다.</Typography></Box>
          : current.length === 0 ? <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography sx={{ fontSize: 18, fontWeight: 850 }}>
                {watchCount === 0 ? '등록된 감시종목이 없습니다.' : '이 분류에서 조회된 변화가 없습니다.'}
              </Typography>
              <Typography sx={{ color: C.muted, mt: 1, fontSize: 13.5, lineHeight: 1.75 }}>
                {watchCount === 0 ? '먼저 종목을 등록하면 실제 변화 기록을 확인할 수 있습니다.' :
                  '이 결과는 조회된 데이터 범위 기준이며, 위험이나 변화가 전혀 없다는 뜻은 아닙니다.'}
              </Typography>
              {watchCount === 0 && <Button onClick={() => navigate('/watch')} sx={{ mt: 1.5, textTransform: 'none' }}>관심종목 등록하기</Button>}
            </Box>
          : current.map((item, index) => <React.Fragment key={item.key}>
              {index > 0 && <Divider/>}
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={.8}>
                      <Typography sx={{ fontSize: 13, color: C.muted, fontWeight: 760 }}>{item.name} · {item.ticker}</Typography>
                      {item.grade !== 'INFO' && <Chip label={item.grade === 'DANGER' ? '위험 신호' : '주의 신호'} size="small"
                        sx={{ fontWeight: 820, bgcolor: item.grade === 'DANGER' ? '#FFF0F1' : '#FFF6E7',
                          color: item.grade === 'DANGER' ? C.risk : '#A1660B' }}/>}
                    </Stack>
                    <Typography sx={{ fontSize: { xs: 17, md: 18 }, fontWeight: 840, mt: 1, lineHeight: 1.5 }}>{item.title}</Typography>
                    <Typography sx={{ color: C.muted, fontSize: 13.5, mt: 1, lineHeight: 1.8 }}>{item.detail}</Typography>
                    {(item.before || item.after) && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.2, fontSize: 12.5 }}>
                      {item.before && <Typography sx={{ color: C.muted, fontSize: 12.5 }}>이전: {item.before}</Typography>}
                      {item.after && <Typography sx={{ color: C.navy, fontWeight: 750, fontSize: 12.5 }}>현재: {item.after}</Typography>}
                    </Box>}
                  </Box>
                  <Box sx={{ flexShrink: 0, minWidth: 110, display: 'flex', flexDirection: { xs: 'row', sm: 'column' },
                    justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'flex-end' }, gap: 1 }}>
                    <Typography sx={{ color: C.unknown, fontSize: 12 }}>{timeText(item.at)}</Typography>
                    <Button size="small" endIcon={<ArrowForwardRoundedIcon/>} onClick={() => view(item)}
                      sx={{ textTransform: 'none', fontWeight: 850, whiteSpace: 'nowrap' }}>변화·근거 확인</Button>
                  </Box>
                </Stack>
              </Box>
            </React.Fragment>)}
      </Box>
      <Typography sx={{ color: C.unknown, fontSize: 12, mt: 2, lineHeight: 1.8 }}>
        서버 조회 범위 밖의 과거 기록은 이 화면에 나타나지 않습니다. 관심종목 저장은 실제 주식 보유 또는 푸시 수신 동의를 뜻하지 않습니다.
      </Typography>
    </Box>
  </Box>;
}
